import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { useState, useEffect, useId } from "react";
import type {
  CategoryAttributeResponse,
  CreateCategoryAttributeRequest,
} from "@/services/api";
import {
  useAttributeValues,
  AttributeValuesSection,
  prepareAttributeValues,
  validateAttributeData,
} from "./shared";

interface AttributeEditDialogProps {
  open: boolean;
  attribute: CategoryAttributeResponse | null;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (id: string | number, data: CreateCategoryAttributeRequest) => void;
}

export function AttributeEditDialog({
  open,
  attribute,
  isLoading,
  onOpenChange,
  onSubmit,
}: AttributeEditDialogProps) {
  const [name, setName] = useState("");
  const {
    values,
    setValues,
    draggedIndex,
    dragOverIndex,
    handleAddValue,
    handleRemoveValue,
    handleValueChange,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetDragState,
  } = useAttributeValues();

  const descriptionId = useId();

  useEffect(() => {
    if (attribute) {
      setName(attribute.name);
      if (attribute.values && attribute.values.length > 0) {
        const sortedValues = [...attribute.values].sort(
          (a, b) => a.order - b.order
        );
        setValues(
          sortedValues.map((v) => ({
            id: String(v.id),
            value: v.value,
            order: v.order,
          }))
        );
      } else {
        setValues([{ id: "1", value: "", order: 1 }]);
      }
    }
    resetDragState();
  }, [attribute, setValues, resetDragState]);

  if (!attribute) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateAttributeData(name, values)) return;

    const validValues = prepareAttributeValues(values);
    if (validValues.length === 0) return;

    onSubmit(attribute.id, {
      name: name.trim(),
      values: validValues,
    });
  };

  const hasValidData = validateAttributeData(name, values);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Edit Attribute</DialogTitle>
          <DialogDescription>
            Update the attribute information below. You can modify the name and
            values.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800">Name</label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter attribute name"
            />
          </div>

          <AttributeValuesSection
            values={values}
            draggedIndex={draggedIndex}
            dragOverIndex={dragOverIndex}
            onValueChange={handleValueChange}
            onRemove={handleRemoveValue}
            onAdd={handleAddValue}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !hasValidData}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
